import { Slice } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query';
import createApiSlice, { ApiSliceState } from '../../../../common/redux/api-slice';
import { AuthActionPayload, QuestionnaireActionPayload } from '../../auth-slice/types';
import { transformAuthenticatedResponse } from './transformers';
import {
    ApiResponse,
    AuthenticatedRequest,
    GoogleSigninResponse,
    PingSessionRequest,
    RegisterRequest,
    SendPageViewRequest,
    UpdateUserRequest,
} from './types';

const api = createApi({
    baseQuery: fetchBaseQuery({
        baseUrl: window.location.origin,
    }),
    refetchOnMountOrArgChange: true,
    endpoints: (builder) => ({
        googleLogin: builder.mutation<AuthActionPayload, GoogleSigninResponse>({
            query: (data) => ({
                url: 'api/index.php?method=login',
                method: 'POST',
                body: JSON.stringify(data),
            }),
            transformResponse: transformAuthenticatedResponse,
        }),

        emailLogin: builder.query<AuthActionPayload, {}>({
            query: (data) => ({
                url: 'api/index.php?method=emailLogin',
                method: 'POST',
                body: JSON.stringify(data),
            }),
            transformResponse: transformAuthenticatedResponse,
            transformErrorResponse: (response: {
                status: number;
                data: { data: { errorMessage: string } };
            }) => {
                return { errorMessage: response.data.data.errorMessage };
            },
        }),

        emailRegister: builder.query<AuthActionPayload, {}>({
            query: (data) => ({
                url: 'api/index.php?method=createAccount',
                method: 'POST',
                body: JSON.stringify(data),
            }),
            transformResponse: transformAuthenticatedResponse,
            transformErrorResponse: (response: {
                status: number;
                data: { data: { errorMessage: string } };
            }) => {
                return { errorMessage: response.data.data.errorMessage };
            },
        }),

        recoverPassword: builder.query<AuthActionPayload, {}>({
            query: (data) => ({
                url: 'api/index.php?method=recoverPassword',
                method: 'POST',
                body: JSON.stringify(data),
            }),
        }),

        resetPassword: builder.query<AuthActionPayload, {}>({
            query: (data) => ({
                url: 'api/index.php?method=setPassword',
                method: 'POST',
                body: JSON.stringify(data),
            }),
        }),
        validateToken: builder.mutation<AuthActionPayload, AuthenticatedRequest>({
            query: (data) => ({
                url: 'api/index.php?method=validate-token',
                method: 'POST',
                body: JSON.stringify(data),
            }),
            transformResponse: transformAuthenticatedResponse,
        }),

        register: builder.mutation<AuthActionPayload, RegisterRequest>({
            query: (data) => ({
                url: 'accountUser/',
                method: 'POST',
                body: JSON.stringify({ action: 'register', ...data }),
            }),
            transformResponse: transformAuthenticatedResponse,
        }),

        updateUser: builder.mutation<AuthActionPayload, UpdateUserRequest>({
            query: (data) => ({
                url: 'api/index.php?method=update-user',
                method: 'POST',
                body: JSON.stringify({ action: 'update', ...data }),
            }),
            transformResponse: transformAuthenticatedResponse,
        }),

        deleteAccount: builder.mutation<ApiResponse, AuthenticatedRequest>({
            query: (data) => ({
                url: 'accountUser/',
                method: 'POST',
                body: JSON.stringify({ action: 'deactivate', ...data }),
            }),
        }),

        saveQuestionnaire: builder.mutation<void, QuestionnaireActionPayload>({
            query: (data) => ({
                url: 'api/index.php?method=save-questionnaire',
                method: 'POST',
                body: JSON.stringify({ ...data }),
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
        }),
    }),
});

export const apiStatusSlice = createApiSlice('apiStatus', api.endpoints) as Slice<
    ApiSliceState,
    {},
    'apiStatus'
>;

export default api;
